import React, { Fragment } from "react";
import { Button } from "../ui/button";
import InstagramIcon from "@/assets/svgs/instagram-icon";
import { Text } from "react-native";
import * as ImagePicker from "expo-image-picker";
import { useMutation } from "@tanstack/react-query";
import { useConvexMutation } from "@convex-dev/react-query";
import { api } from "../utils/convex-api";
import Toast from "react-native-toast-message";

function ProfilePhotoUploader() {
  const { mutateAsync: generateUploadUrl, isPending: isGeneratingUploadUrl } =
    useMutation({
      mutationFn: useConvexMutation(
        api.tutor.profileSettings.mutations.generateUploadUrl
      ),
    });
  const { mutateAsync: updateUserImage, isPending: isUpdatingUserImage } =
    useMutation({
      mutationFn: useConvexMutation(
        api.tutor.profileSettings.mutations.updateUserImage
      ),
    });
  const { mutateAsync: uploadFile, isPending: isUploadingFile } = useMutation({
    mutationFn: async ({
      uploadUrl,
      file,
      mimeType,
    }: {
      uploadUrl: string;
      file: Blob;
      mimeType: string;
    }) => {
      const response = await fetch(uploadUrl, {
        method: "POST",
        body: file,
        headers: {
          "Content-Type": mimeType,
        },
      });
      if (response.ok) {
        const { storageId } = await response.json();
        if (storageId) {
          await updateUserImage({
            imageId: storageId,
          });
        }
      }
    },
  });
  const isPending = isGeneratingUploadUrl || isUpdatingUserImage || isUploadingFile;

  const pickImage = async () => {
    const result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ["images"],
      allowsEditing: true,
      quality: 1,
      allowsMultipleSelection: false,
      selectionLimit: 1,
      aspect: [1, 1],
    });
    const [asset] = result.assets ?? [];
    if (asset) {
      if (asset.mimeType !== "image/jpeg" && asset.mimeType !== "image/png") {
        Toast.show({type: "error", text1: "Only JPEG and PNG images are supported"});
        return;
      }
      if (asset.fileSize && asset.fileSize > 5 * 1024 * 1024) {
        Toast.show({type: "error", text1: "File size cannot exceed 5MB"});
        return;
      }
      if (!asset.file) {
        Toast.show({type: "error", text1: "File not found"});
        return;
      }

      const uploadUrl = await generateUploadUrl({});
      await uploadFile({
        uploadUrl,
        file: asset.file,
        mimeType: asset.mimeType,
      });
    }
  };

  return (
    <Fragment>
      <Button size="sm" intent="primary" outline icon onPress={pickImage} disabled={isPending}>
        <Button.Icon as={InstagramIcon} pathClassName="stroke-teal-600" />
        <Button.Text intent="primary" className="text-sm">
          Upload professional photo
        </Button.Text>
      </Button>
      <Text className="text-sm text-gray-800 leading-[1.5] font-normal font-geologica-regular">
      Max file size: 5mb. Supported formats: JPG, PNG, GIF
      </Text>
    </Fragment>
  );
}

export default ProfilePhotoUploader;
